"use client";

import { IconCode, IconCopy } from "@tabler/icons-react";
import { type ReactNode, useState } from "react";
import {
  INTEGRATION_CODE_KEY_PLACEHOLDER,
  IntegrationCodeSnippets,
} from "@/components/integration-code/integration-code-snippets";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { OnboardingProviderParam } from "@/lib/search-params/nuqs.onboarding";

const SHEET_CODE_REGION_CLASS = "min-h-0 flex-1 overflow-hidden";

type IntegrationCodeSheetProps = {
  /** Optional custom trigger; defaults to the dashboard outline button. */
  trigger?: ReactNode;
  showTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  newApiKey?: { name: string; rawKey: string } | null;
};

/**
 * Right-side panel with the same OpenAI/Anthropic + language snippet experience as onboarding.
 * Optional pasted key is substituted into snippets; empty falls back to YOUR_API_KEY.
 */
export const IntegrationCodeSheet = ({
  trigger,
  showTrigger = true,
  open: controlledOpen,
  onOpenChange,
  newApiKey = null,
}: IntegrationCodeSheetProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [provider, setProvider] = useState<OnboardingProviderParam>("openai");
  const [apiKey, setApiKey] = useState("");
  const [copied, setCopied] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const handleOpenChange = onOpenChange ?? setUncontrolledOpen;

  const apiKeyForSnippets = newApiKey?.rawKey || apiKey.trim() || INTEGRATION_CODE_KEY_PLACEHOLDER;

  const handleCopy = async () => {
    if (!newApiKey) return;
    try {
      await navigator.clipboard.writeText(newApiKey.rawKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button variant="outline" className="w-full sm:w-auto">
              <IconCode className="size-4" />
              View integration code
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="flex min-h-0 h-[min(900px,calc(100dvh-2rem))] max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 gap-4 border-b border-border p-6">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>{newApiKey ? "Save your API key" : "Integration code"}</DialogTitle>
            <DialogDescription>
              {newApiKey
                ? "Safely store a record of your API key, you won’t be able to view it again."
                : "Copy ready-to-run code snippets for the OpenAI or Anthropic SDK."}
            </DialogDescription>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor={newApiKey ? "created-api-key" : "integration-code-api-key"} className="text-sm font-medium">
              {newApiKey?.name ?? "API key"}
            </label>
            {newApiKey ? (
              <div className="flex items-center gap-2">
                <code
                  id="created-api-key"
                  className="min-w-0 flex-1 truncate rounded border bg-background px-3 py-2 font-mono text-sm"
                >
                  {newApiKey.rawKey}
                </code>
                <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <IconCopy className="size-4" />
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            ) : (
              <Input
                id="integration-code-api-key"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="Paste an API key to include it in code snippets"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                className="font-mono text-sm"
              />
            )}
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6">
          <IntegrationCodeSnippets
            provider={provider}
            onProviderChange={setProvider}
            apiKeyForSnippets={apiKeyForSnippets}
            codeRegionClassName={SHEET_CODE_REGION_CLASS}
            className="flex min-h-0 flex-1 flex-col"
          />
        </div>
        <DialogFooter className="shrink-0 p-6 pt-0">
          <Button type="button" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
